/**
 * StrategicResultCard - Tabbed card for completed strategic analysis results
 *
 * Displays strategic analysis results with a tabbed interface:
 * - Collapsed: Compact header with topic, recommendation summary, confidence level
 * - Expanded: Tabbed view with Summary, Ghost Deck, Debate, Workstreams, Conflicts, Market Data
 */

import { useState, useMemo } from 'react';
import {
  Target, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
  Zap, ChevronRight, Brain, FileText, Scale, Layers, Database,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import { MarkdownContent } from './MarkdownContent';
import { ExportMenu } from './ExportMenu';
import { ConfidenceGauge } from '@/components/strategic/ConfidenceGauge';
import { GhostDeckViewer } from '@/components/strategic/GhostDeckViewer';
import { DebateSummaryPanel } from '@/components/strategic/DebateSummaryPanel';
import { WorkstreamBreakdownPanel } from '@/components/strategic/WorkstreamBreakdownPanel';
import { ConflictMapPanel } from '@/components/strategic/ConflictMapPanel';
import { CompetitorDataPanel } from '@/components/strategic/CompetitorDataPanel';
import type {
  StrategicDeliverable,
  StressTestResult,
  StrategicIntensity,
  StrategicWorkstream,
  StrategicConflict,
  DebateArgument,
  MediatorConsensus,
  GhostDeckSlide,
  CompetitorData,
} from '@/hooks/useStrategicStream';
import type { ExportFormat } from '@/types/researchModes';
import { handleStrategicExport, type StrategicExportOptions } from '@/utils/exportUtils';

// ============================================================================
// TYPES
// ============================================================================

interface TabDef {
  id: string;
  label: string;
  icon: typeof FileText;
  count?: number;
}

interface StrategicResultCardProps {
  deliverable: StrategicDeliverable;
  stressTestResults: StressTestResult[];
  topic: string;
  intensity: StrategicIntensity;
  totalCreditsUsed?: number;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
  // Rich data — optional for backward compatibility
  workstreams?: StrategicWorkstream[];
  conflicts?: StrategicConflict[];
  debateArguments?: DebateArgument[];
  debateRounds?: number;
  mediatorConsensus?: MediatorConsensus;
  ghostDeck?: GhostDeckSlide[];
  competitorData?: CompetitorData[];
  query?: string;
  startedAt?: string;
  completedAt?: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function StrategicResultCard({
  deliverable,
  stressTestResults,
  topic,
  intensity,
  totalCreditsUsed,
  isCollapsed = false,
  onToggleCollapse,
  className,
  workstreams,
  conflicts,
  debateArguments,
  debateRounds,
  mediatorConsensus,
  ghostDeck,
  competitorData,
  query,
  startedAt,
  completedAt,
}: StrategicResultCardProps) {
  const [activeTab, setActiveTab] = useState('summary');

  const killSheet = deliverable.killSheet;
  const confidenceLevel = killSheet?.confidenceLevel || '';

  // Tab tooltip descriptions
  const TAB_TOOLTIPS: Record<string, string> = {
    summary: 'Executive summary and key findings',
    deck: 'Investor slide deck presentation',
    debate: 'Pro vs con debate arguments',
    workstreams: 'Parallel research workstreams',
    conflicts: 'Conflicting findings across sources',
    market: 'Competitor and market data',
  };

  // Build available tabs based on data presence
  const tabs = useMemo<TabDef[]>(() => {
    const t: TabDef[] = [{ id: 'summary', label: 'Summary', icon: FileText }];
    if (ghostDeck && ghostDeck.length > 0)
      t.push({ id: 'deck', label: 'Ghost Deck', icon: Layers, count: ghostDeck.length });
    if (debateArguments && debateArguments.length > 0)
      t.push({ id: 'debate', label: 'Debate', icon: Scale, count: debateArguments.length });
    if (workstreams && workstreams.length > 0)
      t.push({ id: 'workstreams', label: 'Workstreams', icon: Target, count: workstreams.length });
    if (conflicts && conflicts.length > 0)
      t.push({ id: 'conflicts', label: 'Conflicts', icon: AlertTriangle, count: conflicts.length });
    if (competitorData && competitorData.length > 0)
      t.push({ id: 'market', label: 'Market Data', icon: Database, count: competitorData.length });
    return t;
  }, [ghostDeck, debateArguments, workstreams, conflicts, competitorData]);

  // Export handler (unchanged)
  const handleExport = async (format: ExportFormat) => {
    const exportOptions: StrategicExportOptions = {
      query: query || topic,
      topic,
      intensity,
      workstreams: workstreams || [],
      conflicts: conflicts || [],
      stressTestResults,
      debateArguments: debateArguments || [],
      debateRounds: debateRounds || 0,
      mediatorConsensus,
      ghostDeck: ghostDeck || [],
      deliverable,
      competitorData: competitorData || [],
      startedAt,
      completedAt,
    };
    await handleStrategicExport(format, exportOptions);
  };

  // ========== COLLAPSED STATE ==========
  if (isCollapsed) {
    return (
      <div
        className={cn(
          'relative w-full bg-surface border overflow-hidden rounded-xl transition-all duration-300',
          'border-v-teal-light/30',
          className
        )}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-v-teal-light/20">
                <Target className="w-4 h-4 text-v-teal-light" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-sm text-text-primary uppercase tracking-wider">
                    Strategic Blueprint
                  </span>
                  {confidenceLevel && (
                    <Tooltip content="Confidence rating of strategic recommendation" position="top">
                      <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-v-teal-light/20 text-v-teal-light cursor-help">
                        {confidenceLevel}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <p className="text-xs text-text-muted mt-0.5 truncate">{topic}</p>
              </div>
            </div>
            {onToggleCollapse && (
              <Tooltip content="Expand full analysis" position="left">
                <button
                  onClick={onToggleCollapse}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors flex-shrink-0"
                >
                  <ChevronDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Details</span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ========== EXPANDED STATE ==========
  return (
    <div
      className={cn(
        'relative w-full bg-surface border overflow-hidden rounded-xl transition-all duration-300',
        'border-v-teal-light/30',
        className
      )}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-subtle bg-bar-bg/50">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-v-teal-light/20">
              <Target className="w-4 h-4 text-v-teal-light" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-sm text-text-primary uppercase tracking-wider">
                Strategic Blueprint
              </span>
              <span className="font-mono text-xs text-text-muted uppercase tracking-wider">
                {intensity}
              </span>
              {confidenceLevel && (
                <Tooltip content="Confidence rating of strategic recommendation" position="top">
                  <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-v-teal-light/20 text-v-teal-light cursor-help">
                    {confidenceLevel}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportMenu
              onExport={handleExport}
              reportTitle={`Strategic Analysis: ${topic}`}
            />
            {onToggleCollapse && (
              <Tooltip content="Collapse to summary" position="left">
                <button
                  onClick={onToggleCollapse}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                  <span className="hidden sm:inline">Collapse</span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Tab bar — only show when there's more than just "Summary" */}
      {tabs.length > 1 && (
        <div className="relative border-b border-border-subtle bg-bar-bg/30">
          <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto scrollbar-none" role="tablist">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <Tooltip key={tab.id} content={TAB_TOOLTIPS[tab.id] || tab.label} position="top">
                  <button
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap',
                      activeTab === tab.id
                        ? 'bg-v-teal/20 text-v-teal-light'
                        : 'text-text-muted hover:text-text-primary hover:bg-surface-light/50'
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {tab.label}
                    {tab.count != null && (
                      <span className={cn(
                        'px-1.5 py-0.5 rounded text-[10px]',
                        activeTab === tab.id ? 'bg-v-teal/30 text-v-teal-light' : 'bg-surface-light text-text-muted'
                      )}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                </Tooltip>
              );
            })}
          </div>
          {/* Right-edge fade when tabs overflow */}
          <div className="absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-black/60 to-transparent pointer-events-none" />
        </div>
      )}

      {/* Tab content */}
      <div className="p-4" role="tabpanel">
        {activeTab === 'summary' && (
          <SummaryTabContent
            deliverable={deliverable}
            killSheet={killSheet}
            confidenceLevel={confidenceLevel}
            stressTestResults={stressTestResults}
          />
        )}
        {activeTab === 'deck' && ghostDeck && (
          <GhostDeckViewer slides={ghostDeck} />
        )}
        {activeTab === 'debate' && debateArguments && (
          <DebateSummaryPanel
            debateArguments={debateArguments}
            mediatorConsensus={mediatorConsensus}
            debateRounds={debateRounds}
          />
        )}
        {activeTab === 'workstreams' && workstreams && (
          <WorkstreamBreakdownPanel workstreams={workstreams} />
        )}
        {activeTab === 'conflicts' && conflicts && (
          <ConflictMapPanel conflicts={conflicts} />
        )}
        {activeTab === 'market' && competitorData && (
          <CompetitorDataPanel competitors={competitorData} />
        )}
      </div>

      {/* Footer — latency and cost (always visible) */}
      <div className="flex items-center justify-between text-xs text-text-muted px-4 pb-3 pt-1 border-t border-border/30 mx-4">
        {deliverable.totalLatencySeconds && (
          <span>Analysis completed in {deliverable.totalLatencySeconds.toFixed(1)}s</span>
        )}
        {totalCreditsUsed != null && totalCreditsUsed > 0 && (
          <span className="font-mono opacity-70">
            {Math.ceil(totalCreditsUsed)} credits
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SUMMARY TAB (extracted from original body)
// ============================================================================

function SummaryTabContent({
  deliverable,
  killSheet,
  confidenceLevel,
  stressTestResults,
}: {
  deliverable: StrategicDeliverable;
  killSheet: StrategicDeliverable['killSheet'];
  confidenceLevel: string;
  stressTestResults: StressTestResult[];
}) {
  // Derive headwinds and tailwinds
  const headwinds: string[] = [];
  const tailwinds: string[] = [];

  if (killSheet?.risks) headwinds.push(...killSheet.risks);
  if (killSheet?.opportunities) tailwinds.push(...killSheet.opportunities);

  stressTestResults.forEach(result => {
    if (result.testResult === 'invalidated' || result.confidence < 0.5) {
      headwinds.push(`Assumption challenged: ${result.assumptionStatement} (${Math.round(result.confidence * 100)}% confidence)`);
    } else if (result.testResult === 'validated' && result.confidence >= 0.7) {
      tailwinds.push(`Validated: ${result.assumptionStatement} (${Math.round(result.confidence * 100)}% confidence)`);
    }
  });

  const assumptions = stressTestResults.map(result => ({
    statement: result.assumptionStatement,
    status: result.testResult,
    confidence: result.confidence,
    evidenceFor: result.evidenceFor,
    evidenceAgainst: result.evidenceAgainst,
  }));

  return (
    <div className="space-y-4">
      {/* GROUP 1: "What We Recommend" */}

      {/* Strategic Recommendation (first - most prominent) */}
      {killSheet?.recommendation && (
        <div className="rounded-lg bg-gradient-to-r from-v-teal-light/10 to-v-gold/5 border border-v-teal-light/20 p-4">
          <div className="text-v-teal-light/80 mb-2 flex items-center gap-2 uppercase tracking-wide font-medium text-xs">
            <Target className="w-4 h-4" />
            Strategic Recommendation
            {confidenceLevel && (
              <ConfidenceGauge level={confidenceLevel} compact className="ml-2" />
            )}
          </div>
          <div className="text-text-primary text-[15px] leading-relaxed">
            <MarkdownContent content={killSheet.recommendation} className="text-[15px]" />
          </div>
          {confidenceLevel && (
            <div className="mt-3 pt-3 border-t border-v-teal-light/10">
              <ConfidenceGauge level={confidenceLevel} />
            </div>
          )}
        </div>
      )}

      {/* Next Steps (right after recommendation) */}
      {killSheet?.nextSteps && killSheet.nextSteps.length > 0 && (
        <div className="rounded-lg bg-v-steel/10 border border-v-steel/20 p-4">
          <div className="text-v-steel/80 mb-2 flex items-center gap-2 uppercase tracking-wide font-medium text-xs">
            <ChevronRight className="w-4 h-4" />
            Recommended Next Steps
          </div>
          <ol className="space-y-2 text-[15px]">
            {killSheet.nextSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-text-primary">
                <span className="w-5 h-5 rounded-full bg-v-steel/20 text-v-steel flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {i + 1}
                </span>
                <MarkdownContent content={step} className="flex-1 [&_p]:inline text-[15px]" inline />
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Divider between "What We Recommend" and "What We Found" */}
      {(killSheet?.recommendation || killSheet?.nextSteps) && (
        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-surface-raised/50" />
          <span className="text-xs text-text-muted uppercase tracking-wider font-medium">Supporting Analysis</span>
          <div className="flex-1 h-px bg-surface-raised/50" />
        </div>
      )}

      {/* GROUP 2: "What We Found" */}

      {/* Executive Summary */}
      {(killSheet?.executiveSummary || deliverable.executiveSummary) && (
        <div className="rounded-lg bg-v-teal-light/10 border border-v-teal-light/20 p-4">
          <div className="text-v-teal-light/80 mb-2 flex items-center gap-2 uppercase tracking-wide font-medium text-xs">
            <FileText className="w-4 h-4" />
            Executive Summary
          </div>
          <div className="text-text-primary text-[15px] leading-relaxed">
            <MarkdownContent content={killSheet?.executiveSummary || deliverable.executiveSummary || ''} className="text-[15px]" />
          </div>
        </div>
      )}

      {/* Key Findings */}
      {killSheet?.keyFindings && killSheet.keyFindings.length > 0 && (
        <div className="rounded-lg bg-v-teal/10 border border-v-teal/20 p-4">
          <div className="text-v-teal/80 mb-2 flex items-center gap-2 uppercase tracking-wide font-medium text-xs">
            <CheckCircle2 className="w-4 h-4" />
            Key Findings
            <span className="ml-1 px-2 py-0.5 rounded bg-v-teal/20 text-v-teal-light text-xs">
              {killSheet.keyFindings.length}
            </span>
          </div>
          <div className="max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-v-teal/20 scrollbar-track-transparent">
            <ul className="space-y-2 text-[15px]">
              {killSheet.keyFindings.map((finding, i) => (
                <li key={i} className="flex items-start gap-2 text-text-primary">
                  <CheckCircle2 className="w-4 h-4 text-v-teal mt-0.5 flex-shrink-0" />
                  <MarkdownContent content={finding} className="flex-1 [&_p]:inline text-[15px]" inline />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Headwinds vs Tailwinds */}
      {(headwinds.length > 0 || tailwinds.length > 0) && (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
          {/* Headwinds */}
          <div className="rounded-lg bg-gradient-to-br from-v-crimson/10 to-v-crimson-light/5 border border-v-crimson/20 p-4">
            <div className="text-v-crimson mb-2 flex items-center gap-2 uppercase tracking-wide font-semibold text-xs">
              <AlertTriangle className="w-4 h-4" />
              Headwinds
              <span className="ml-auto px-2 py-0.5 rounded bg-v-crimson/20 text-v-crimson-light text-xs font-normal">
                {headwinds.length}
              </span>
            </div>
            <div className="max-h-[180px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-v-crimson/20 scrollbar-track-transparent">
              {headwinds.length > 0 ? (
                <ul className="space-y-2 text-[15px]">
                  {headwinds.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-text-primary">
                      <span className="w-2 h-2 rounded-full bg-v-crimson mt-1.5 flex-shrink-0" />
                      <MarkdownContent content={item} className="flex-1 [&_p]:inline text-[15px]" inline />
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-text-muted text-center py-2 text-[15px]">No significant headwinds identified</div>
              )}
            </div>
          </div>

          {/* Tailwinds */}
          <div className="rounded-lg bg-gradient-to-br from-v-teal/10 to-v-teal/5 border border-v-teal/20 p-4">
            <div className="text-v-teal mb-2 flex items-center gap-2 uppercase tracking-wide font-semibold text-xs">
              <Zap className="w-4 h-4" />
              Tailwinds
              <span className="ml-auto px-2 py-0.5 rounded bg-v-teal/20 text-v-teal-light text-xs font-normal">
                {tailwinds.length}
              </span>
            </div>
            <div className="max-h-[180px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-v-teal/20 scrollbar-track-transparent">
              {tailwinds.length > 0 ? (
                <ul className="space-y-2 text-[15px]">
                  {tailwinds.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-text-primary">
                      <span className="w-2 h-2 rounded-full bg-v-teal mt-1.5 flex-shrink-0" />
                      <MarkdownContent content={item} className="flex-1 [&_p]:inline text-[15px]" inline />
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-text-muted text-center py-2 text-[15px]">No significant tailwinds identified</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assumptions with Validation Status */}
      {assumptions.length > 0 && (
        <div className="rounded-lg bg-v-gold/10 border border-v-gold/20 p-4">
          <div className="text-v-gold/80 mb-2 flex items-center gap-2 uppercase tracking-wide font-medium text-xs">
            <Brain className="w-4 h-4" />
            Key Assumptions (Stress Tested)
            <span className="ml-1 px-2 py-0.5 rounded bg-v-gold/20 text-v-gold-light text-xs">
              {assumptions.length}
            </span>
          </div>
          <div className="max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-v-gold/20 scrollbar-track-transparent space-y-2">
            {assumptions.map((assumption, i) => (
              <div key={i} className="rounded-lg bg-surface-light/50 border border-border/50 p-3">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="text-text-primary flex-1 text-[15px]">
                    <MarkdownContent content={assumption.statement} className="text-[15px]" />
                  </div>
                  <span className={cn(
                    'px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                    assumption.status === 'validated' ? 'bg-v-teal/20 text-v-teal' :
                    assumption.status === 'invalidated' ? 'bg-v-crimson/20 text-v-crimson' :
                    'bg-v-gold/20 text-v-gold'
                  )}>
                    {assumption.status === 'validated' ? 'Validated' : assumption.status === 'invalidated' ? 'Challenged' : 'Uncertain'}
                    {' '}{Math.round(assumption.confidence * 100)}%
                  </span>
                </div>
                <div className="flex gap-4 text-xs">
                  {assumption.evidenceFor && assumption.evidenceFor.length > 0 && (
                    <div className="text-v-teal/70">
                      <span className="font-medium">Supporting:</span> {assumption.evidenceFor.length} points
                    </div>
                  )}
                  {assumption.evidenceAgainst && assumption.evidenceAgainst.length > 0 && (
                    <div className="text-v-crimson/70">
                      <span className="font-medium">Challenging:</span> {assumption.evidenceAgainst.length} points
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
